import Box from "@material-ui/core/Box";
import Collapse from "@material-ui/core/Collapse";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Link from "@material-ui/core/Link";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import React, {memo, useState} from "react";
import OverlayProgress from "./components/common/Loading";
import ExcelConverter from "./components/ExcelConverter";
import ExcelGenerator from "./components/ExcelGenerator";
import HeaderMappingTable from "./components/HeaderMappingTable";
import MacroEditor from "./components/MacroEditor";
import {KEY_MACRO, LANGUAGE_CODES, VALUE_MACRO} from "./constant";
import useLocalStorageState from "./hooks/useLocalStorageState";

const App = () => {
    const [isLoading, setLoading] = useState(false);
    const [headerMappingExpanded, setHeaderMappingExpanded] = useState(false);
    const [languageCodes, setLanguageCodes] = useLocalStorageState("App.state.languageCodes", LANGUAGE_CODES);
    const [useKeyMacro, setUseKeyMacro] = useLocalStorageState("App.state.useKeyMacro", false);
    const [useValueMacro, setUseValueMacro] = useLocalStorageState("App.state.useValueMacro", false);
    const [keyMacro, setKeyMacro] = useLocalStorageState("App.state.keyMacro", KEY_MACRO);
    const [valueMacro, setValueMacro] = useLocalStorageState("App.state.valueMacro", VALUE_MACRO);

    const handleExpand = () => {
        setHeaderMappingExpanded(!headerMappingExpanded);
    };

    const handleReset = () => {
        setLanguageCodes(LANGUAGE_CODES);
    };

    const handleUpdate = (name, languageCode) => {
        const newLanguageCodes = [...languageCodes];
        const index = newLanguageCodes.findIndex(value => value.name === name);
        if (index < 0) return;
        if (languageCodes.find((value, i) => value.name === languageCode.name && i !== index)) return alert("Duplicate header.");
        if (languageCodes.find((value, i) => value.android === languageCode.android && i !== index)) return alert("Duplicate Android language.");
        if (languageCodes.find((value, i) => value.ios === languageCode.ios && i !== index)) return alert("Duplicate iOS language.");
        newLanguageCodes[index] = languageCode;
        setLanguageCodes(newLanguageCodes);
        return newLanguageCodes;
    };

    const handleDelete = name => {
        const newLanguageCodes = languageCodes.filter(value => value.name !== name);
        setLanguageCodes(newLanguageCodes);
    };

    const handleAdd = languageCode => {
        if (languageCodes.find(value => value.name === languageCode.name)) return alert("Duplicate header.");
        if (languageCodes.find(value => value.android === languageCode.android)) return alert("Duplicate Android language.");
        if (languageCodes.find(value => value.ios === languageCode.ios)) return alert("Duplicate iOS language.");
        const newLanguageCodes = [...languageCodes, languageCode];
        setLanguageCodes(newLanguageCodes);
        return languageCode;
    };

    const handleMacroReset = name => {
        switch (name) {
            case "keyMacro":
                return setKeyMacro(KEY_MACRO);
            case "valueMacro":
                return setValueMacro(VALUE_MACRO);
        }
    };

    const handleCheckBoxChange = event => {
        switch (event.target.name) {
            case "keyMacro":
                return setUseKeyMacro(event.target.checked);
            case "valueMacro":
                return setUseValueMacro(event.target.checked);
        }
    };

    const handleEditorChange = (name, macro) => {
        switch (name) {
            case "keyMacro":
                return setKeyMacro(macro);
            case "valueMacro":
                return setValueMacro(macro);
        }
    };
    return (
        <Box p={{xs: 1, sm: 2}}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Paper>
                        <Box p={{xs: 1, sm: 2}}>
                            <ExcelConverter keyMacro={useKeyMacro ? keyMacro : null}
                                            valueMacro={useValueMacro ? valueMacro : null}
                                            onLoad={setLoading}
                                            languageCodes={languageCodes}/>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12}>
                    <Paper>
                        <Box p={{xs: 1, sm: 2}}>
                            <ExcelGenerator keyMacro={useKeyMacro ? keyMacro : null}
                                            valueMacro={useValueMacro ? valueMacro : null}
                                            onLoad={setLoading}
                                            languageCodes={languageCodes}/>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12}>
                    <Paper>
                        <Box p={{xs: 1, sm: 2}}>
                            <Grid container spacing={2} alignItems={"center"}>
                                <Grid item>
                                    <Typography variant="h4">Header mappings</Typography>
                                </Grid>
                                <Grid item>
                                    <IconButton onClick={handleExpand}>
                                        {headerMappingExpanded ? <ExpandLess/> :
                                            <ExpandMore/>}
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Box>
                        <Box overflow="auto">
                            <Collapse in={headerMappingExpanded}>
                                <HeaderMappingTable languageCodes={languageCodes}
                                                    onReset={handleReset}
                                                    onUpdate={handleUpdate}
                                                    onDelete={handleDelete}
                                                    onAdd={handleAdd}/>
                            </Collapse>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper>
                        <Box p={{xs: 1, sm: 2}}>
                            <MacroEditor
                                useMacro={useKeyMacro}
                                name="keyMacro"
                                label="Use key macro"
                                onReset={handleMacroReset}
                                onCheckBoxChange={handleCheckBoxChange}
                                onEditorChange={handleEditorChange}
                                value={keyMacro}/>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper>
                        <Box p={{xs: 1, sm: 2}}>
                            <MacroEditor
                                useMacro={useValueMacro}
                                name="valueMacro"
                                label="Use value macro"
                                onReset={handleMacroReset}
                                onCheckBoxChange={handleCheckBoxChange}
                                onEditorChange={handleEditorChange}
                                value={valueMacro}/>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12}>
                    <Link
                        href="mailto:jsn_professor@hotmail.com?subject=String table converter feedback">
                        jsn_professor@hotmail.com
                    </Link>
                </Grid>
            </Grid>
            <OverlayProgress isLoading={isLoading}/>
        </Box>
    );
};

export default memo(App);
